import { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DollarSign, Eye, Palette } from 'lucide-react';
import type { PluginBuilderSession } from '../types';
import type { FiasTheme } from '@fias/arche-sdk';
import { getThemeById } from '@fias/arche-sdk';
import { ThemeSelector } from './ThemeSelector';

interface PluginSettings {
  name: string;
  pricing: {
    model: 'free' | 'fixed' | 'per_use' | 'no_base_cost' | 'included';
    priceCents?: number;
  };
  visibility: {
    isListed: boolean;
    isPublicAccess: boolean;
    isFullScreen: boolean;
  };
  themeId: string;
  fontPairingId: string;
  customTheme?: FiasTheme;
}

interface PluginConfigPanelProps {
  session: PluginBuilderSession | null;
  onSettingsChange: (settings: PluginSettings) => void;
  /** Called when the plugin name changes so the header updates immediately */
  onNameChange?: (name: string) => void;
  /** Called when the short description changes so it persists to currentDescription */
  onDescriptionChange?: (description: string) => void;
  /** Called when the expanded description ("About" text) changes */
  onExpandedDescriptionChange?: (expandedDescription: string) => void;
  /** When true, name and descriptions are locked (require re-publish to change) */
  isPublished?: boolean;
  className?: string;
}

function ThemeConfigCard({
  themeId,
  fontPairingId,
  onThemeChange,
  onFontPairingChange,
}: {
  themeId: string;
  fontPairingId: string;
  onThemeChange: (themeId: string, customTheme?: FiasTheme) => void;
  onFontPairingChange: (fontPairingId: string) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const entry = getThemeById(themeId);

  let themeName: string;
  if (themeId === 'ai-auto') themeName = "AI's Choice";
  else if (themeId === 'no-theme') themeName = 'Custom from Scratch';
  else if (themeId === 'custom') themeName = 'Custom';
  else themeName = entry?.name ?? themeId;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Palette className="h-4 w-4" />
          Appearance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm">
            Theme: <span className="font-medium">{themeName}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
            Change
          </Button>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl h-[85vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>Choose a Theme</DialogTitle>
            </DialogHeader>
            <ThemeSelector
              selectedThemeId={themeId}
              onThemeChange={(id, ct) => onThemeChange(id, ct)}
              selectedFontPairingId={fontPairingId}
              onFontPairingChange={onFontPairingChange}
              className="flex-1 min-h-0"
            />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export function PluginConfigPanel({
  session,
  onSettingsChange,
  onNameChange,
  onDescriptionChange,
  onExpandedDescriptionChange,
  isPublished = false,
  className,
}: PluginConfigPanelProps) {
  const currentSettings = (session?.currentSettings || {}) as Partial<PluginSettings> & {
    aiConfigs?: unknown[];
  };
  const hasAiConfigs = ((currentSettings.aiConfigs as unknown[])?.length ?? 0) > 0;

  const [settings, setSettings] = useState<PluginSettings>({
    name: session?.currentName || currentSettings.name || '',
    pricing: currentSettings.pricing || { model: 'free' },
    visibility: currentSettings.visibility || {
      isListed: true,
      isPublicAccess: false,
      isFullScreen: false,
    },
    themeId: ((currentSettings as Record<string, unknown>).themeId as string) ?? 'fias-dark',
    fontPairingId:
      ((currentSettings as Record<string, unknown>).fontPairingId as string) ?? 'system',
    customTheme: (currentSettings as Record<string, unknown>).customTheme as FiasTheme | undefined,
  });

  // Separate local state for name and descriptions so they only commit on blur/Enter
  const [localName, setLocalName] = useState(settings.name);
  const [localDescription, setLocalDescription] = useState(session?.currentDescription || '');
  const [localExpandedDescription, setLocalExpandedDescription] = useState(
    session?.currentExpandedDescription || '',
  );

  const updateSettings = (updates: Partial<PluginSettings>) => {
    const newSettings = { ...settings, ...updates };

    // Auto-disable public access when switching to paid pricing or when AI entities exist
    const isNonFree =
      newSettings.pricing.model !== 'free' &&
      newSettings.pricing.model !== 'included' &&
      newSettings.pricing.model !== 'no_base_cost';
    if (isNonFree && newSettings.visibility.isPublicAccess) {
      newSettings.visibility = { ...newSettings.visibility, isPublicAccess: false };
    }

    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  /** Capitalize the first letter of each word */
  const toTitleCase = (value: string) => value.replace(/\b\w/g, (char) => char.toUpperCase());

  const commitName = useCallback(() => {
    const trimmed = localName.trim();
    if (!trimmed) return;
    const titleCased = toTitleCase(trimmed);
    setLocalName(titleCased);
    updateSettings({ name: titleCased });
    onNameChange?.(titleCased);
  }, [localName, onNameChange]);

  const commitDescription = useCallback(() => {
    const trimmed = localDescription.trim();
    onDescriptionChange?.(trimmed);
  }, [localDescription, onDescriptionChange]);

  const commitExpandedDescription = useCallback(() => {
    const trimmed = localExpandedDescription.trim();
    onExpandedDescriptionChange?.(trimmed);
  }, [localExpandedDescription, onExpandedDescriptionChange]);

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-4', className)}>
      {/* Name & Description */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="plugin-name-config">Arche Name</Label>
            <Input
              id="plugin-name-config"
              type="text"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  commitName();
                  e.currentTarget.blur();
                }
              }}
              placeholder="My Arche"
              disabled={isPublished}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-description-config">Short Description</Label>
            <Textarea
              id="plugin-description-config"
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              onBlur={commitDescription}
              placeholder="One-liner shown on marketplace cards"
              className="resize-none"
              rows={2}
              disabled={isPublished}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-expanded-description-config">About</Label>
            <Textarea
              id="plugin-expanded-description-config"
              value={localExpandedDescription}
              onChange={(e) => setLocalExpandedDescription(e.target.value)}
              onBlur={commitExpandedDescription}
              placeholder="Full description shown on your arche's detail page"
              className="resize-none"
              rows={5}
              disabled={isPublished}
            />
          </div>
          {isPublished && (
            <p className="text-xs text-muted-foreground">
              Name and descriptions are locked after publishing. Re-publish to update them.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Pricing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={
              settings.pricing.model === 'fixed' || settings.pricing.model === 'per_use'
                ? 'fixed'
                : 'no_base_cost'
            }
            onValueChange={(value: 'no_base_cost' | 'fixed') =>
              updateSettings({
                pricing: { ...settings.pricing, model: value },
              })
            }
          >
            <SelectTrigger id="pricing-model">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="no_base_cost">No Base Cost</SelectItem>
              <SelectItem value="fixed">Paid</SelectItem>
            </SelectContent>
          </Select>

          {settings.pricing.model === 'fixed' && (
            <div className="space-y-2">
              <Label htmlFor="plugin-price">Price (cents)</Label>
              <Input
                id="plugin-price"
                type="number"
                min="0"
                value={settings.pricing.priceCents || ''}
                onChange={(e) =>
                  updateSettings({
                    pricing: {
                      ...settings.pricing,
                      priceCents: parseInt(e.target.value) || 0,
                    },
                  })
                }
                placeholder="0"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visibility */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Visibility
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="plugin-listed"
              checked={settings.visibility.isListed}
              onCheckedChange={(checked) =>
                updateSettings({
                  visibility: { ...settings.visibility, isListed: checked as boolean },
                })
              }
            />
            <Label htmlFor="plugin-listed" className="text-sm font-normal">
              Listed (visible in store)
            </Label>
          </div>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="plugin-public"
                checked={settings.visibility.isPublicAccess}
                disabled={hasAiConfigs || settings.pricing.model !== 'free'}
                onCheckedChange={(checked) =>
                  updateSettings({
                    visibility: { ...settings.visibility, isPublicAccess: checked as boolean },
                  })
                }
              />
              <Label
                htmlFor="plugin-public"
                className={cn(
                  'text-sm font-normal',
                  (hasAiConfigs || settings.pricing.model !== 'free') && 'text-muted-foreground',
                )}
              >
                Public Access (viewable when not logged in)
              </Label>
            </div>
            {(hasAiConfigs || settings.pricing.model !== 'free') && (
              <p className="text-xs text-muted-foreground ml-6">
                Only available for free arches that don't require credits.
              </p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="plugin-fullscreen"
              checked={settings.visibility.isFullScreen}
              onCheckedChange={(checked) =>
                updateSettings({
                  visibility: { ...settings.visibility, isFullScreen: checked as boolean },
                })
              }
            />
            <Label htmlFor="plugin-fullscreen" className="text-sm font-normal">
              Full Screen Mode
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Appearance / Theme */}
      <ThemeConfigCard
        themeId={settings.themeId}
        fontPairingId={settings.fontPairingId}
        onThemeChange={(themeId, customTheme) => updateSettings({ themeId, customTheme })}
        onFontPairingChange={(fontPairingId) => updateSettings({ fontPairingId })}
      />
    </div>
  );
}
